<template>
  <div class="field-history-content">
    <template v-if="revisions">
      <template v-if="Object.keys(revisions).length">
        <b-collapse :model-value="false" class="history-entry" animation="slide" v-for="([name, value], index) in sortedRevisions" :key="index">
          <template #trigger="{ open }">
            <div class="history-entry__title" :class="open && 'open'">
              <span>{{ name }}</span>
              <i class="mdi" :class="open ? 'mdi-menu-up' : 'mdi-menu-down'"></i>
            </div>
          </template>
          <template #default>
            <div class="history-entry__content-wrapper">
              <div class="history-entry__content" v-for="(revision, index) in value" :key="index">
                <template v-if="revision.action == 'updated'">
                  <template v-if="isFieldArray(revision.old_value) && isFieldArray(revision.new_value)">
                    <div v-for="(item, idx) in getFieldPairs(revision.old_value, revision.new_value)" :key="idx" class="history-entry__changes-wrapper">
                      <div class="history-entry__change history-entry__change--from">
                        <span class="field-key" v-if="item.old.key">{{ item.old.key }}:</span>
                        <span class="field-value" v-if="item.old.value">{{ formatFieldValue(item.old.value) }}</span>
                      </div>
                      <span class="mdi mdi-arrow-right"></span>
                      <div class="history-entry__change history-entry__change--to">
                        <span class="field-key" v-if="item.new.key">{{ item.new.key }}:</span>
                        <span class="field-value" v-if="item.new.value">{{ formatFieldValue(item.new.value) }}</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="history-entry__changes-wrapper">
                      <div class="history-entry__change history-entry__change--from">
                        <template v-if="isFieldArray(revision.old_value)">
                          <div v-for="(item, idx) in revision.old_value" :key="idx" class="field-item">
                            <span class="field-key">{{ item.key }}:</span>
                            <span class="field-value">{{ formatFieldValue(item.value) }}</span>
                          </div>
                        </template>
                        <template v-else>{{ revision.old_value }}</template>
                      </div>
                      <span class="mdi mdi-arrow-right"></span>
                      <div class="history-entry__change history-entry__change--to">
                        <template v-if="isFieldArray(revision.new_value)">
                          <div v-for="(item, idx) in revision.new_value" :key="idx" class="field-item">
                            <span class="field-key">{{ item.key }}:</span>
                            <span class="field-value">{{ formatFieldValue(item.value) }}</span>
                          </div>
                        </template>
                        <template v-else>{{ revision.new_value }}</template>
                      </div>
                    </div>
                  </template>
                </template>
                <template v-if="revision.action == 'added'">
                  <template v-if="isFieldArray(revision.value)">
                    <div v-for="(item, idx) in revision.value" :key="idx" class="history-entry__changes-wrapper">
                      <span class="mr-2">Added</span>
                      <div class="history-entry__change history-entry__change--to">
                        <span class="field-key">{{ item.key }}:</span>
                        <span class="field-value">{{ formatFieldValue(item.value) }}</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="history-entry__changes-wrapper">
                      <span class="mr-2">Added</span>
                      <div class="history-entry__change history-entry__change--to">{{ revision.value }}</div>
                    </div>
                  </template>
                </template>
                <template v-if="revision.action == 'deleted'">
                  <template v-if="isFieldArray(revision.value)">
                    <div v-for="(item, idx) in revision.value" :key="idx" class="history-entry__changes-wrapper">
                      <span class="mr-2">Removed</span>
                      <div class="history-entry__change history-entry__change--from">
                        <span class="field-key">{{ item.key }}:</span>
                        <span class="field-value">{{ formatFieldValue(item.value) }}</span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="history-entry__changes-wrapper">
                      <span class="mr-2">Removed</span>
                      <div class="history-entry__change history-entry__change--from">{{ revision.value }}</div>
                    </div>
                  </template>
                </template>
                <p class="history-entry__change-date">by {{ revision.username }} @ {{ $helpers.formatDate(revision.created_at)}}</p>
              </div>
            </div>
          </template>
        </b-collapse>
      </template>
      <empty-state v-else title="Action history is empty" size="small"></empty-state>
    </template>
  </div>
</template>

<script>
import to from "@/lib/to";
import EmptyState from "./EmptyState.vue";

export default {
  name: "FieldHistoryContent",
  props: ['objectType', 'objectId'],
  components: { EmptyState },
  async beforeMount() {
    const [err, response] = await to(this.$http.get(`/revisions/${this.objectType}/${this.objectId}`))
    if (!err) {
      this.revisions = response.data
    }
  },
  data() {
    return {
      revisions: null
    }
  },
  computed: {
    sortedRevisions() {
      if (!this.revisions) return []

      return Object.entries(this.revisions).sort((a, b) => {
        return a[0].toLowerCase().localeCompare(b[0].toLowerCase())
      })
    }
  },
  methods: {
    isFieldArray(value) {
      return Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'key' in value[0]
    },
    formatFieldValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ')
      }
      return value
    },
    getFieldPairs(oldFields, newFields) {
      const pairs = []
      const maxLength = Math.max(oldFields.length, newFields.length)

      for (let i = 0; i < maxLength; i++) {
        const oldItem = oldFields[i] || { key: '', value: '' }
        const newItem = newFields[i] || { key: '', value: '' }
        
        if (this.formatFieldValue(oldItem.value) !== this.formatFieldValue(newItem.value)) {
          pairs.push({
            old: oldItem,
            new: newItem
          })
        }
      }
      
      return pairs
    }
  }
}
</script>

<style scoped lang="scss">
.field-history-content {
  padding: 10px 0;
}
.history-entry {
  margin-bottom: 10px;
}
.history-entry__title {
  text-transform: capitalize;
  background: #fff;
  padding: 10px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  i {
    font-size: 23px;
  }
  &.open {
    border-radius: 8px 8px 0 0;
  }
}
.history-entry__content-wrapper {
  border-top: 1px solid #dce8ff;
  background: #FFFFFF;
  padding: 10px;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 8px 8px;
}
.history-entry__content {
  font-size: 14px;
  margin-bottom: 15px;
  &:last-child {
    margin-bottom: 0;
  }
}
.history-entry__change-date {
  text-align: right;
  font-size: 10px;
  opacity: 0.7;
}
.history-entry__change {
  padding: 5px;
  border-radius: 8px;
  font-size: 13px;
  word-break: break-word;
  &.history-entry__change--from {
    background: #ffb8b8;
  }
  &.history-entry__change--to {
    background: #b3ffb7;
  }
}
.history-entry__changes-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  .mdi {
    margin: 0 4px;
  }
}
.history__empty {
  margin-top: 10px;
  text-align: center;
  color: #999;
}

.field-item {
  margin-bottom: 4px;
  &:last-child {
    margin-bottom: 0;
  }
}
.field-key {
  font-weight: 500;
  margin-right: 5px;
}
.field-value {
  word-break: break-word;
}
</style> 